import classNames from 'classnames'
import { type LucideIcon } from 'lucide-react'
import { forwardRef, type ButtonHTMLAttributes } from 'react'

export type IconButtonVariant = 'default' | 'ghost' | 'danger'
export type IconButtonSize    = 'sm' | 'md' | 'lg'

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  icon:      LucideIcon
  /** Obrigatório — acessibilidade. Descreve a ação do botão para leitores de tela. */
  label:     string
  variant?:  IconButtonVariant
  size?:     IconButtonSize
}

const variantClasses: Record<IconButtonVariant, string> = {
  default: 'bg-surface-alt text-text-main hover:text-secondary hover:bg-surface',
  ghost:   'bg-transparent text-text-muted hover:text-text-main hover:bg-surface-alt/50',
  danger:  'bg-transparent text-red-500 hover:bg-red-500/10',
}

/** min-w/min-h garante touch target mínimo de 44px em md/lg */
const sizeClasses: Record<IconButtonSize, string> = {
  sm: 'min-w-[36px] min-h-[36px] rounded-lg',
  md: 'min-w-[44px] min-h-[44px] rounded-xl',
  lg: 'min-w-[44px] min-h-[44px] rounded-2xl',
}

const iconSizeMap: Record<IconButtonSize, number> = { sm: 14, md: 18, lg: 22 }

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  function IconButton(
    { icon: Icon, label, variant = 'default', size = 'md', disabled, className, ...props },
    ref,
  ) {
    return (
      <button
        ref={ref}
        aria-label={label}
        disabled={disabled}
        className={classNames(
          'inline-flex items-center justify-center',
          'transition-all duration-200',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
          'active:scale-95',
          variantClasses[variant],
          sizeClasses[size],
          disabled && 'opacity-40 cursor-not-allowed',
          className,
        )}
        {...props}
      >
        <Icon size={iconSizeMap[size]} aria-hidden="true" />
      </button>
    )
  },
)

IconButton.displayName = 'IconButton'
